
import { defineComponent, computed, PropType, ref, reactive, watch, onBeforeUpdate } from 'vue'
import { ElForm } from 'element-plus'
import { AxiosResponse } from 'axios'

import JrDialog from '@/components/jr-parts/JrDialog.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import DepartmentSelect from '@/components/inputs/DepartmentSelect.vue'
import SectionSelect from '@/components/inputs/SectionSelect.vue'
import UserSelect from '@/components/inputs/UserSelect.vue'
import FormItemDisabled from '@/components/layout/FormItemDisabled.vue'
import JrText from '@/components/jr-parts/JrText.vue'

import { messageAddressRule } from '@/constants/rules'
import { Address } from '@/types/clipboard'
import { useStore, Store } from '@/store'
import { addMessageQueue, MESSAGE_TYPE_TOAST, MESSAGE_TYPE_ERROR } from '@/plugins/messageQueue'
import Message from '@/services/message'

import { Department, Section, User, MessageAddress } from '@/api'
import { ListRequest as DepartmentListRequest } from '@/requests/department'
import { ListRequest as SectionListRequest } from '@/requests/section'
import { ListRequest as UserListRequest, ReadRequest as UserReadRequest } from '@/requests/user'

// デフォルト行数
const DEFAULT_ROW_COUNT = 5

let rowIndex = 0

/**
 * 宛先に管理用のindex・エラー保持変数を付与した型
 */
interface AddressRow extends Address {
  row_index: number
  duplicate: string | null
}

/**
 * 宛先行から管理用のrow_indexを省き、新たな配列・オブジェクトにして返す
 */
const deleteRowIndex = (addresses: AddressRow[]): Address[] => {
  return addresses.map((address) => {
    const deleted = Object.assign(address)
    delete deleted.row_index
    return deleted as Address
  })
}

/**
 * 新行を生成する
 */
const generateRow = (address: Address): AddressRow => {
  return {
    row_index: rowIndex++,
    duplicate: null,
    ...address,
  }
}

/**
 * 宛先から管理用のrow_indexを付与して新たな配列・オブジェクトにして返す
 */
const addRowIndex = (addresses: Address[]): AddressRow[] => {
  return addresses.map((address) => {
    return generateRow(address)
  })
}

/**
 * 宛先が入力されているか判定する
 */
const isEnteredAddress = (address: AddressRow): boolean => {
  // 保守区、箇所が入力されており全社員か社員・メールアドレスが入力されているか
  return address.department !== null && address.section !== null && address.user !== null && address.email != ''
}

/**
 * MessageAddress配列から宛先行を作成する
 */
const messageAddressToAddressRow = (messageAddresses: MessageAddress[]) => {
  return messageAddresses.map((ma) => {
    return generateRow({ ...ma })
  })
}

/**
 * 宛先行から重複を排除したMessageAddress配列を作成する
 */
const addressRowToMessageAddress = async (addressRows: AddressRow[]): Promise<MessageAddress[] | null> => {
  const results: MessageAddress[] = []

  // 非同期処理があるためfor-ofで回す
  for (const address of addressRows) {
    if (!isEnteredAddress(address)) {
      // 未入力行は飛ばす
      continue
    }

    // 入力された社員を入れる
    if (results.findIndex((result) => result.user === address.user) < 0) {
      // 既に結果に存在しない場合、追加
      results.push({
        is_applicant: false,
        department: address.department,
        department_code: address.department_code,
        department_name: address.department_name,
        section: address.section,
        section_code: address.section_code,
        section_name: address.section_name,
        user: address.user,
        user_name: address.user_name,
        email: address.email,
      })
    }
  }

  return results
}

/**
 * メールアドレスの齟齬を確認する。以下の条件でtrueとなる
 * 1. メールアドレスが同じ社員に対して入力されており、それぞれ異なる場合
 * 2. 箇所内社員全てを選び、他行でその箇所内の社員のメールアドレスを現在のマスタと異なるものを入力した場合
 * 同じユーザーが複数指定された場合でも、メールアドレスが全て同じであればfalseとなる
 */
const checkDuplicateAddress = async (addresses: AddressRow[]): Promise<boolean> => {
  addresses.forEach((address) => {
    // エラーをリセット
    address.duplicate = null
  })
  let isDuplicate = false
  for (const address of addresses) {
    const duplicates = addresses.filter((check_address) => {
      // 入力されており、社員コードが同じか、社員全てで箇所コードが同じ宛先が存在する場合
      return isEnteredAddress(address) && address.user != null && address.user === check_address.user
    })
    if (duplicates.length > 1) {
      // 重複が1件以上あれば、メールアドレスが変更されているかチェック
      const emails: string[] = []
      duplicates.forEach((d) => {
        if (d.email !== '') {
          // メールアドレスを抽出
          emails.push(d.email)
        }
      })
      if (emails.length === 0) {
        // 全て未指定なら問題無し
        continue
      }
      if (duplicates.length === emails.length && emails.every((email) => email === emails[0])) {
        // 重複している全てがメールアドレス同じなら問題無し
        continue
      }
      if (emails.length >= 2 && !emails.every((email) => email === emails[0])) {
        // 2つ以上メールアドレスが入力されており、それぞれ異なるものならエラー
        duplicates.forEach((d) => {
          isDuplicate = true
          d.duplicate = Message.bind(Message.codes.DUPLICATE, { attribute: '送信先' })
        })
        continue
      }
      // その他の場合(1つ以上は社員全て、もう1つ以上はメールアドレス入力有り)、社員の正規のメールアドレスを取得
      const user = duplicates.find((d) => d.user != null)
      if (user == null || user.user == null) {
        // ここに入るパターンは不具合。1つはメールアドレスが入力されておりユーザーも特定できているはずだが
        // 重複しているユーザーの中から社員コードが取得できない
        throw new Error('check mailaddress duplicate error in Address Select Dialog.')
      }
      // ユーザー情報を取得し、正規のメールアドレスを取得
      const request = new UserReadRequest(user.user)
      await request.exec()
      if (request.success) {
        const { data } = request.getResponse() as AxiosResponse<User>
        if (!emails.every((email) => email === data.user_email)) {
          // 正規のメールアドレスと異なるメールアドレスが存在する場合、エラー
          isDuplicate = true
          duplicates.forEach((d) => {
            d.duplicate = Message.bind(Message.codes.DUPLICATE, { attribute: '送信先' })
          })
        }
      } else {
        // ユーザー情報が取得できない場合、処理の続行が不可(BaseRequestからエラーメッセージが出る)
        isDuplicate = true
      }
      // 全てのメールアドレスが正規のものと同じなら問題無し
    }
  }
  return isDuplicate
}

/**
 * 宛先選択Dialog
 * 配列でMessageAddressを受け取り、新たな配列を返すため
 * 親で変更を受け取る際は:modelValue="messageAddresses" @update:modelValue="updateMessageAddress($event)"
 * のようにして、updateMessageAddress関数内でsplice(0)でリセット、pushして保持させないとwatch等で変更が検知できない
 */
export default defineComponent({
  name: 'AddressSelect',
  emits: ['update:modelValue'],
  components: {
    JrDialog,
    JrButton,
    DepartmentSelect,
    SectionSelect,
    UserSelect,
    FormItemDisabled,
    JrText,
  },
  props: {
    // 宛先配列
    modelValue: {
      type: Array as PropType<MessageAddress[]>,
      required: true,
    },
    // 編集可能か
    isEdit: {
      type: Boolean,
      required: true,
    },
    // 保守区固定
    fixedDepartment: {
      type: Number,
      required: false,
      default: null,
    },
  },
  setup(props, context) {
    const store = useStore() as Store
    // ダイアログ開閉
    const dialogVisible = ref<boolean>(false)
    // 宛先一覧
    const addresses = reactive<AddressRow[]>([])
    // 宛先ref
    let addressRefs = reactive<InstanceType<typeof ElForm>[]>([])
    const setAddressRef = (el: InstanceType<typeof ElForm>) => {
      if (el) {
        addressRefs.push(el)
      }
    }

    onBeforeUpdate(() => {
      addressRefs.splice(0)
    })

    // 複写情報有り無し
    const hasClipboard = computed(() => {
      return store.getters['user/getAddresses'] != null
    })

    // 保守区固定モード
    const fixedDepartmentMode = computed(() => {
      return props.fixedDepartment != null
    })

    // 保守区固定モードで使用する保守区情報
    const department = {
      department_name: null as null | string,
      department_code: null as null | string,
    }

    /**
     * 行の追加を行う
     */
    const add = () => {
      addresses.push(
        generateRow({
          is_applicant: false, // 宛先選択に来る場合は申請者は対象ではない
          email: '',
          // 保守区固定モードならその保守区を入れる
          department: props.fixedDepartment,
          department_code: department.department_code,
          department_name: department.department_name,
          section_code: null,
          section_name: null,
          user_name: null,
          section: null,
          user: null,
        })
      )
    }

    // ダイアログ開閉時の動作
    watch(dialogVisible, async (newVisible, oldVisible) => {
      if (newVisible && !oldVisible) {
        // ダイアログを開いた際
        if (props.fixedDepartment != null) {
          // 保守区指定有ならその保守区情報を取得
          const departmentRequest = new DepartmentListRequest()
          await departmentRequest.exec()
          if (!departmentRequest.success) {
            // 取得失敗時は何もせずダイアログを閉じる
            dialogVisible.value = false
            return
          }
          const { data } = departmentRequest.getResponse() as AxiosResponse<Department[]>

          const detail = data.find((d) => d.department_id === props.fixedDepartment)
          if (detail != null) {
            department.department_name = detail.department_name
            department.department_code = detail.department_code
          } else {
            // 保守区が無ければエラーを出しダイアログを閉じる
            addMessageQueue(MESSAGE_TYPE_ERROR, Message.bind(Message.codes.NOT_EXIST_OLD, { attribute: '保守区' }))
            department.department_name = null
            department.department_code = null
            dialogVisible.value = false
            return
          }
        } else {
          // 保守区指定無しならnull
          department.department_name = null
          department.department_code = null
        }

        // 親から与えられた宛先配列に入れなおす
        addresses.splice(0)
        addresses.push(...messageAddressToAddressRow(props.modelValue))
        if (addresses.length === 0) {
          // 0件の場合、デフォルト行数分追加する
          for (let i = 0; i < DEFAULT_ROW_COUNT; i++) {
            add()
          }
        }
      } else if (!newVisible && oldVisible) {
        // ダイアログを閉じた際、宛先をリセットする
        addresses.splice(0)
      }
    })

    /**
     * 宛先の複写を行う
     */
    const copy = () => {
      const copyTargets = addresses.filter((address) => {
        // 保守区・箇所が選択されており全社員or社員名・メールアドレスが入力されているものをコピーする
        return isEnteredAddress(address)
      })
      if (copyTargets.length === 0) {
        // 対象が0件の場合はエラー
        addMessageQueue(MESSAGE_TYPE_ERROR, Message.codes.NONE_COPY_TARGET)
        return
      }

      // storeに登録
      store.commit('user/setAddresses', deleteRowIndex(copyTargets))
      addMessageQueue(MESSAGE_TYPE_TOAST, Message.bind(Message.codes.COPY_COMPLETE, { target: '送信先' }))
    }

    /**
     * 宛先の貼り付けを行う
     */
    const paste = async () => {
      const storeAddresses = store.getters['user/getAddresses']
      if (storeAddresses == null) {
        return
      }

      // 保守区取得
      const departmentRequest = new DepartmentListRequest()
      await departmentRequest.exec()
      if (!departmentRequest.success) {
        return
      }
      const departmentResponse = departmentRequest.getResponse() as AxiosResponse<Department[]>
      const departments = departmentResponse.data
      // 箇所取得
      const sectionRequest = new SectionListRequest()
      await sectionRequest.exec()
      if (!sectionRequest.success) {
        return
      }
      const sectionResponse = sectionRequest.getResponse() as AxiosResponse<Section[]>
      const sections = sectionResponse.data
      // 社員取得
      const userRequest = new UserListRequest()
      await userRequest.exec()
      if (!userRequest.success) {
        return
      }
      const userResponse = userRequest.getResponse() as AxiosResponse<User[]>
      const users = userResponse.data

      // 存在する宛先のみ抽出
      const existingAddresses = storeAddresses.filter((address) => {
        // 保守区・箇所・社員がマスタにあるか検索
        return (
          departments.findIndex((d) => d.department_id === address.department) >= 0 &&
          sections.findIndex((s) => s.section_id === address.section) >= 0 &&
          // 社員全てか、社員していならその社員が存在するか
          users.findIndex((u) => u.shain_code === address.user) >= 0
        )
      })
      if (existingAddresses.length === 0) {
        // 全て存在しない場合、エラーメッセージを出し何もしない
        addMessageQueue(MESSAGE_TYPE_ERROR, Message.bind(Message.codes.NOT_EXIST_OLD, { attribute: '宛先' }))
        return
      } else if (storeAddresses.length !== existingAddresses.length) {
        // 存在しない宛先があった場合、通知を出す
        addMessageQueue(MESSAGE_TYPE_ERROR, Message.bind(Message.codes.NOT_EXIST_OLD, { attribute: '一部の宛先' }))
      }
      if (fixedDepartmentMode.value) {
        // 保守区固定モードならその保守区のみ
        const addressesInDepartment = existingAddresses.filter(
          (address) => address.department === props.fixedDepartment
        )
        if (addressesInDepartment.length === 0) {
          // 貼り付ける保守区が無ければエラー表示し何もしない
          addMessageQueue(MESSAGE_TYPE_ERROR, Message.bind(Message.codes.NOT_PASTE_ALLOWED, { attribute: '保守区' }))
          return
        } else if (existingAddresses.length !== addressesInDepartment.length) {
          // 貼り付けられていない保守区があればエラー表示
          addMessageQueue(MESSAGE_TYPE_ERROR, Message.bind(Message.codes.NOT_PASTE_ALLOWED, { attribute: '保守区' }))
        }
        addresses.splice(0)
        addresses.push(...addRowIndex(addressesInDepartment))
      } else {
        // その他は全て貼り付け
        addresses.splice(0)
        addresses.push(...addRowIndex(existingAddresses))
      }
    }

    /**
     * 決定
     */
    const decide = async () => {
      // バリデーション
      let validResult = true
      addressRefs.forEach((formRef) => {
        // バリデーションチェック
        formRef.validate((valid) => {
          if (!valid) {
            validResult = false
          }
        })
      })

      if (validResult && addresses.filter((address) => isEnteredAddress(address)).length === 0) {
        // 0件ならエラー
        addMessageQueue(MESSAGE_TYPE_ERROR, Message.bind(Message.codes.V_REQUIRED_SELECT, { attribute: '送信先' }))
        return
      }

      //重複チェック
      const isDuplicate = await checkDuplicateAddress(addresses)

      // OKならemit
      if (validResult && !isDuplicate) {
        // 整形
        const results = await addressRowToMessageAddress(addresses)
        if (results != null) {
          context.emit('update:modelValue', results)
          dialogVisible.value = false
        }
      }
    }

    const setAllUser = (users: User[], index: number) => {
      const tmpAddress = addresses[index]
      // 「全て」を選択した行は不要なので削除
      addresses.splice(index, 1)
      // 「全て」を選択した行位置にループでユーザーを挿入
      for (const [pos, user] of users.entries()) {
        addresses.splice(
          index + pos,
          0,
          generateRow({
            is_applicant: false,
            email: user.user_email,
            department: tmpAddress.department,
            department_code: tmpAddress.department_code,
            department_name: tmpAddress.department_name,
            section_code: tmpAddress.section_code,
            section_name: tmpAddress.section_name,
            user_name: user.user_name,
            section: tmpAddress.section,
            user: user.shain_code,
          })
        )
      }
      // 新しい配列を作成し、重複を排除しながら要素を追加
      const newAddresses: AddressRow[] = []
      for (const address of addresses) {
        if (
          newAddresses.findIndex((newAddress) => newAddress.email === address.email) < 0 ||
          (address.user_name === null && address.email === '')
        ) {
          newAddresses.push(address)
        }
      }
      // 元の配列を初期化し、新たに作成した配列を展開
      addresses.splice(0)
      addresses.splice(0, 0, ...newAddresses)
    }

    return {
      dialogVisible,
      setAddressRef,
      addresses,
      messageAddressRule,
      fixedDepartmentMode,
      add,
      copy,
      paste,
      hasClipboard,
      decide,
      setAllUser,
    }
  },
})
